(() => {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  ready(() => {
    document.documentElement.classList.add('hey2-pdp-v2-active');

    const interactiveSelector = [
      'button',
      'a',
      '[role="button"]',
      'input[type="button"]',
      'input[type="submit"]',
      '[onclick]',
      '[class*="button"]',
      '[class*="Button"]'
    ].join(',');

    /* The LensAdvisor app uses custom elements rather than native buttons.
       Its storefront control is normally <lensadvizor-btn>, which the generic
       button selector below cannot see. Keep this selector explicit so the
       visible Hey2 action always reaches the real app control. */
    const lensAdvisorSelector = [
      'lensadvizor-btn[data-lensadvizor-btn]',
      'lensadvizor-btn.la-select-lenses-btn',
      'lensadvizor-quick-buy[data-la-quick-buy]',
      '[data-lensadvizor-btn]',
      '[data-la-quick-buy]',
      '#la-select-lenses-btn',
      '.la-select-lenses-btn'
    ].join(',');

    const getLabel = (element) => `${element?.textContent || ''} ${element?.getAttribute?.('aria-label') || ''} ${element?.getAttribute?.('title') || ''} ${element?.id || ''} ${element?.className || ''}`
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

    const isCustomPrescriptionAction = (element) => Boolean(
      element?.closest?.('[data-hey2-open-lensadvisor], .hey2-prescription-only-action')
    );

    const isLensAdvisorElement = (element) => Boolean(element?.matches?.(lensAdvisorSelector));

    const scoreLensAdvisorTrigger = (element) => {
      const label = getLabel(element);
      if (isCustomPrescriptionAction(element)) return -1;
      let value = 0;
      if (isLensAdvisorElement(element)) value += 220;
      if (label.includes('shop prescription with glasses')) value += 100;
      if (label.includes('prescription') && label.includes('glasses')) value += 55;
      if (label.includes('lensadvisor') || label.includes('select-lenses')) value += 45;
      if (label.includes('customize') && label.includes('lens')) value += 35;
      if (label.includes('select') && label.includes('lens')) value += 25;
      if (label.includes('lens')) value += 5;
      if (element.tagName === 'BUTTON') value += 3;
      return value;
    };

    const getExplicitLensAdvisorTriggers = (root = document) => Array.from(
      root.querySelectorAll(lensAdvisorSelector)
    ).filter((element) => !isCustomPrescriptionAction(element));

    const isActionElement = (element) => isLensAdvisorElement(element) || element.matches(
      'button, a, [role="button"], input[type="button"], input[type="submit"]'
    );

    const getLensAdvisorCandidates = (root = document) => Array.from(
      root.querySelectorAll(`${lensAdvisorSelector},${interactiveSelector}`)
    )
      .filter((element, index, all) => all.indexOf(element) === index)
      /* Text matching must only inspect real controls. A parent such as
         .buy-buttons includes the app button's text and must never be hidden. */
      .filter(isActionElement)
      .map((element, index) => ({
        element,
        index,
        value: scoreLensAdvisorTrigger(element)
      }))
      .filter((item) => item.value >= 20)
      .sort((a, b) => b.value - a.value || a.index - b.index);

    /* LensAdvisor renders its blue control outside the app block in some
       storefront builds. Find that real control globally, keep it mounted so
       its modal still works, and hide only its visual surface. */
    const findLensAdvisorTrigger = () => {
      const app = document.querySelector('.hey2-lensadvisor-action');
      if (app) {
        const explicit = getExplicitLensAdvisorTriggers(app);
        if (explicit.length) return explicit[0];
        const local = getLensAdvisorCandidates(app);
        if (local.length) return local[0].element;
      }

      const explicit = getExplicitLensAdvisorTriggers(document);
      if (explicit.length) return explicit[0];
      const global = getLensAdvisorCandidates(document);
      return global.length ? global[0].element : null;
    };

    const concealNativeLensAdvisor = (element) => {
      if (!element || isCustomPrescriptionAction(element)) return;
      const target = element.closest?.('lensadvizor-btn, lensadvizor-quick-buy, button, a, [role="button"]') || element;
      if (isCustomPrescriptionAction(target)) return;
      target.classList.add('hey2-lensadvisor-native-hidden');
      target.setAttribute('aria-hidden', 'true');
      target.setAttribute('tabindex', '-1');
    };

    const hideNativeLensAdvisor = () => {
      /* Hide only the app's entry controls. Do not inspect modal buttons here:
         LensAdvisor mutates the DOM while its flow is open. */
      getExplicitLensAdvisorTriggers(document).forEach(concealNativeLensAdvisor);

      getLensAdvisorCandidates(document)
        .filter(({ element }) => getLabel(element).includes('shop prescription with glasses'))
        .forEach(({ element }) => concealNativeLensAdvisor(element));
    };

    const releasePageScroll = () => {
      document.documentElement.classList.add('hey2-pdp-v2-active');
      if (document.body) document.body.removeAttribute('data-page-rendering');
    };

    releasePageScroll();
    hideNativeLensAdvisor();

    window.addEventListener('load', releasePageScroll, { once: true });

    let appObserverTimer;
    const appObserver = new MutationObserver(() => {
      window.clearTimeout(appObserverTimer);
      appObserverTimer = window.setTimeout(() => {
        hideNativeLensAdvisor();
      }, 80);
    });
    if (document.body) appObserver.observe(document.body, { childList: true, subtree: true });

    /* Keep horizontal tab/nav items visible without scrollIntoView(). The
       latter is allowed to move the document vertically and was causing the
       page to jump back to Overview when that section left the viewport. */
    const keepInHorizontalScroller = (element) => {
      let container = element?.parentElement;
      while (container && container !== document.body) {
        const styles = window.getComputedStyle(container);
        if (container.scrollWidth > container.clientWidth + 1 && /auto|scroll/.test(styles.overflowX)) {
          const elementRect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const delta = elementRect.left - containerRect.left - (container.clientWidth - elementRect.width) / 2;
          if (Math.abs(delta) > 2) {
            container.scrollTo({ left: container.scrollLeft + delta, behavior: 'smooth' });
          }
          return;
        }
        container = container.parentElement;
      }
    };

    const openLensAdvisor = (source) => {
      if (!source || source.dataset.hey2LensadvisorOpening === 'true') return;

      source.dataset.hey2LensadvisorOpening = 'true';
      source.setAttribute('aria-busy', 'true');

      let opened = false;
      const finish = () => {
        source.removeAttribute('aria-busy');
        delete source.dataset.hey2LensadvisorOpening;
        document.removeEventListener('LensAdvizor:selectLensModal:open', onLensAdvisorOpen);
      };
      const onLensAdvisorOpen = () => {
        opened = true;
        finish();
      };
      const attempts = [0, 160, 520, 1300, 2800, 4800];

      document.addEventListener('LensAdvizor:selectLensModal:open', onLensAdvisorOpen, { once: true });

      attempts.forEach((delay, index) => {
        window.setTimeout(() => {
          if (opened) return;
          const target = findLensAdvisorTrigger();
          if (target && typeof target.click === 'function') {
            /* LensAdvisor attaches its modal launcher directly to this custom
               element. Calling the actual element avoids duplicating or
               guessing the app's private API. */
            target.click();
          }
          if (index === attempts.length - 1 && !opened) {
            window.setTimeout(finish, 360);
          }
        }, delay);
      });
    };

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-hey2-open-lensadvisor]');
      if (!trigger) return;
      event.preventDefault();
      openLensAdvisor(trigger);
    });

    document.querySelectorAll('[data-hey2-scroll]').forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        const selector = trigger.getAttribute('href') || trigger.dataset.hey2Scroll;
        if (!selector || !selector.startsWith('#')) return;
        const target = document.querySelector(selector);
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });

    document.querySelectorAll('[data-hey2-tabs]').forEach((tabs) => {
      const buttons = Array.from(tabs.querySelectorAll('[role="tab"]'));
      const panels = Array.from(tabs.querySelectorAll('[role="tabpanel"]'));
      if (!buttons.length || !panels.length) return;

      const activate = (button, focus = false) => {
        const panelId = button.getAttribute('aria-controls');
        buttons.forEach((item) => {
          const selected = item === button;
          item.setAttribute('aria-selected', selected ? 'true' : 'false');
          item.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          const selected = panel.id === panelId;
          panel.hidden = !selected;
          panel.classList.toggle('is-active', selected);
        });
        if (focus) button.focus({ preventScroll: true });
      };

      buttons.forEach((button, index) => {
        button.addEventListener('click', () => activate(button));
        button.addEventListener('keydown', (event) => {
          let nextIndex = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (index + 1) % buttons.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (index - 1 + buttons.length) % buttons.length;
          if (event.key === 'Home') nextIndex = 0;
          if (event.key === 'End') nextIndex = buttons.length - 1;
          if (nextIndex === null) return;
          event.preventDefault();
          activate(buttons[nextIndex], true);
          keepInHorizontalScroller(buttons[nextIndex]);
        });
      });

      activate(buttons.find((button) => button.getAttribute('aria-selected') === 'true') || buttons[0]);
    });

    document.querySelectorAll('[data-hey2-language-browser]').forEach((browser) => {
      const input = browser.querySelector('[data-hey2-language-search]');
      const clear = browser.querySelector('[data-hey2-language-clear]');
      const status = browser.querySelector('[data-hey2-language-status]');
      const empty = browser.querySelector('[data-hey2-language-empty]');
      const items = Array.from(browser.querySelectorAll('[data-hey2-language]'));
      if (!input || !status || !items.length) return;
      const defaultStatus = 'Search the full language list';

      const normalize = (value) => value
        .toLocaleLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();

      const render = () => {
        const query = normalize(input.value);
        let matches = 0;
        items.forEach((item) => {
          const label = normalize(item.dataset.hey2Language || item.textContent || '');
          const visible = !query || label.includes(query);
          item.hidden = !visible;
          if (visible) matches += 1;
        });

        if (clear) clear.hidden = !query;
        if (empty) empty.hidden = matches !== 0;
        status.textContent = query
          ? `${matches} ${matches === 1 ? 'match' : 'matches'}`
          : defaultStatus;
      };

      input.addEventListener('input', render);
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !input.value) return;
        input.value = '';
        render();
      });
      clear?.addEventListener('click', () => {
        input.value = '';
        input.focus({ preventScroll: true });
        render();
      });
      render();
    });

    document.querySelectorAll('[data-hey2-review-thumbnail]').forEach((thumbnail) => {
      const media = thumbnail.closest('[data-hey2-review-video]');
      if (!media) return;
      const markLoaded = () => {
        if (thumbnail.naturalWidth > 0) media.classList.add('has-thumbnail');
      };
      if (thumbnail.complete) markLoaded();
      thumbnail.addEventListener('load', markLoaded, { once: true });
    });

    document.addEventListener('click', (event) => {
      const playButton = event.target.closest('[data-hey2-review-play]');
      if (!playButton) return;

      const media = playButton.closest('[data-hey2-review-video]');
      const videoId = media?.dataset.youtubeId;
      if (!media || !videoId) return;

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
      iframe.title = playButton.getAttribute('aria-label') || 'Leion Hey2 reviewer video';
      iframe.loading = 'lazy';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      media.replaceChildren(iframe);
    });

    document.querySelectorAll('[data-hey2-nav]').forEach((nav) => {
      const links = Array.from(nav.querySelectorAll('a[href^="#"]'));
      const targets = links
        .map((link) => document.querySelector(link.getAttribute('href')))
        .filter(Boolean);
      if (!targets.length || !('IntersectionObserver' in window)) return;

      const linkById = new Map(links.map((link) => [link.getAttribute('href').slice(1), link]));
      const observer = new IntersectionObserver((entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        links.forEach((link) => link.removeAttribute('aria-current'));
        const active = linkById.get(visible.target.id);
        if (active) {
          active.setAttribute('aria-current', 'location');
          keepInHorizontalScroller(active);
        }
      }, { rootMargin: '-28% 0px -62% 0px', threshold: [0, 0.08, 0.2] });
      targets.forEach((target) => observer.observe(target));
    });

    const hero = document.querySelector('.hey2-pdp-hero');
    document.querySelectorAll('[data-hey2-mobile-buy]').forEach((bar) => {
      if (!hero || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(([entry]) => {
        bar.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0.08 });
      observer.observe(hero);
    });
  });
})();
